import Color from "color";
import cloneDeep from "lodash/cloneDeep";
import { flattenKeys } from "@/utils/flattenKeys";
import { getFontValuesByFontVariant } from "@/utils/style/getFontValuesByFontVariant";
import { getHEXColorByColorVariant } from "@/utils/style/getHEXColorByColorVariant";
import { getSizeByPredefinedSize } from "@/utils/style/getSizeByPredefinedSize";
import type { Font, PredefinedFont, Theme } from "@/types/theme";

type ShortTheme<T = PredefinedFont> = Omit<
  Theme<T>,
  "id" | "name" | "description" | "setup" | "customCSS"
>;
type ClientTheme = ShortTheme<Font>;

function transformPredefinedValues(theme: ShortTheme): ClientTheme {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function iter(o: any) {
    Object.keys(o).forEach((k) => {
      if (o[k].hasOwnProperty("cssType")) {
        switch (o[k]["cssType"]) {
          case "size":
            o[k] = getSizeByPredefinedSize(o[k]);
            break;

          case "color":
            const color = getHEXColorByColorVariant(theme, o[k].value);
            if (!color) {
              o[k] = "";
            } else {
              o[k] = color.startsWith("var(")
                ? color
                : Color(color)
                    .alpha((o[k].opacity ?? 100) / 100)
                    .toString();
            }
            break;

          case "font":
            o[k] = getFontValuesByFontVariant(theme, o[k].value);
            break;

          default:
            break;
        }
        return;
      }

      if (o[k] !== null && typeof o[k] === "object") {
        iter(o[k]);
      }
    });

    return o;
  }

  return iter(theme) as ClientTheme;
}

export function transformThemeConfigToCSSVariables(
  theme: Theme,
  prefix: string[] = [],
): Record<string, string> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id, name, description, setup, customCSS, ...themeConfig } =
    cloneDeep(theme);

  return flattenKeys(transformPredefinedValues(themeConfig), prefix);
}
